"use strict";

var __extends = this && this.__extends || function () {
  var _extendStatics = function extendStatics(d, b) {
    _extendStatics = Object.setPrototypeOf || {
      __proto__: []
    } instanceof Array && function (d, b) {
      d.__proto__ = b;
    } || function (d, b) {
      for (var p in b) if (Object.prototype.hasOwnProperty.call(b, p)) d[p] = b[p];
    };
    return _extendStatics(d, b);
  };
  return function (d, b) {
    _extendStatics(d, b);
    function __() {
      this.constructor = d;
    }
    d.prototype = b === null ? Object.create(b) : (__.prototype = b.prototype, new __());
  };
}();
var __importDefault = this && this.__importDefault || function (mod) {
  return mod && mod.__esModule ? mod : {
    "default": mod
  };
};
Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.BaseModal = void 0;
var react_1 = __importDefault(require("react"));
var prop_types_1 = __importDefault(require("prop-types"));
var warning_1 = __importDefault(require("../util/warning"));
var react_prop_types_1 = require("../react-prop-types");
var portal_1 = require("./portal");
var modal_manager_1 = require("./modal-manager");
var owner_document_1 = require("./utils/owner-document");
var add_event_listener_1 = require("./utils/add-event-listener");
var add_focus_listener_1 = require("./utils/add-focus-listener");
var dom_helpers_1 = require("../util/dom-helpers");
var get_container_1 = require("./utils/get-container");
var modalManager = new modal_manager_1.ModalManager();
/**
 * Love them or hate them, `<Modal/>` provides a solid foundation for creating dialogs, lightboxes, or whatever else.
 * The Modal component renders its `children` node in front of a backdrop component.
 *
 * The Modal offers a few helpful features over using just a `<Portal/>` component and some styles:
 *
 * - Manages dialog stacking when one-at-a-time just isn't enough.
 * - Creates a backdrop, for disabling interaction below the modal.
 * - It properly manages focus; moving to the modal content, and keeping it there until the modal is closed.
 * - It disables scrolling of the page content while open.
 * - Adds the appropriate ARIA roles are automatically.
 * - Easily pluggable animations via a `<Transition/>` component.
 *
 * Note that, in the same way the backdrop element prevents users from clicking or interacting
 * with the page content underneath the Modal, Screen readers also need to be signaled to not to
 * interact with page content while the Modal is open. To do this, we use a common technique of applying
 * the `aria-hidden='true'` attribute to the non-Modal elements in the Modal `container`. This means that for
 * a Modal to be truly modal, it should have a `container` that is _outside_ your app's
 * React hierarchy (such as the default: document.body).
 */
var BaseModal = /** @class */function (_super) {
  __extends(BaseModal, _super);
  function BaseModal(props) {
    var _this = _super.call(this, props) || this;
    _this.manager = modalManager;
    _this._isMounted = false;
    _this.onShow = function () {
      var doc = owner_document_1.ownerDocument(_this);
      var container = get_container_1.getContainer(_this.props.container, doc.body);
      modalManager.add(_this, container, _this.props.containerClassName);
      _this._onDocumentKeyupListener = add_event_listener_1.addEventListener(doc, 'keyup', _this.handleDocumentKeyUp);
      _this._onFocusinListener = add_focus_listener_1.addFocusListener(_this.enforceFocus);
      _this.focus();
      if (_this.props.onShow) {
        _this.props.onShow();
      }
    };
    _this.onHide = function () {
      modalManager.remove(_this);
      _this._onDocumentKeyupListener.remove();
      _this._onFocusinListener.remove();
      _this.restoreLastFocus();
    };
    _this.setMountNode = function (ref) {
      _this.mountNode = ref ? ref.getMountNode() : ref;
    };
    _this.setModalNode = function (ref) {
      _this.modalNode = ref;
    };
    _this.handleHidden = function () {
      var _a;
      var args = [];
      for (var _i = 0; _i < arguments.length; _i++) {
        args[_i] = arguments[_i];
      }
      _this.setState({
        exited: true
      });
      _this.onHide();
      if (_this.props.onExited) {
        (_a = _this.props).onExited.apply(_a, args);
      }
    };
    _this.handleBackdropClick = function (e) {
      if (e.target !== e.currentTarget) {
        return;
      }
      var _a = _this.props,
        onHide = _a.onHide,
        backdrop = _a.backdrop,
        onBackdropClick = _a.onBackdropClick;
      if (onBackdropClick) {
        onBackdropClick(e);
      }
      if (backdrop === true && onHide) {
        onHide();
      }
    };
    _this.handleDocumentKeyUp = function (e) {
      var _a = _this.props,
        keyboard = _a.keyboard,
        onEscapeKeyUp = _a.onEscapeKeyUp,
        onHide = _a.onHide;
      if (keyboard && e.keyCode === 27 && _this.isTopModal()) {
        if (onEscapeKeyUp) {
          onEscapeKeyUp(e);
        }
        if (onHide) {
          onHide();
        }
      }
    };
    _this.checkForFocus = function () {
      if (dom_helpers_1.inDOM) {
        _this.lastFocus = dom_helpers_1.activeElement();
      }
    };
    _this.focus = function () {
      setTimeout(function () {
        var autoFocus = _this.props.autoFocus;
        var modalContent = _this.getDialogElement();
        var current = dom_helpers_1.activeElement(owner_document_1.ownerDocument(_this));
        var focusInModal = current && dom_helpers_1.contains(modalContent, current);
        if (modalContent && autoFocus && !focusInModal) {
          _this.lastFocus = current;
          if (!modalContent.hasAttribute('tabIndex')) {
            modalContent.setAttribute('tabIndex', '-1');
            warning_1.default(false, 'The modal content node does not accept focus. ' + 'For the benefit of assistive technologies, the tabIndex of the node is being set to "-1".');
          }
          modalContent.focus();
        }
      }, 0);
    };
    _this.restoreLastFocus = function () {
      // Support: <=IE11 doesn't support `focus()` on svg elements (RB: #917)
      if (_this.lastFocus && _this.lastFocus.focus) {
        _this.lastFocus.focus();
        _this.lastFocus = undefined;
      }
    };
    _this.enforceFocus = function () {
      var enforceFocus = _this.props.enforceFocus;
      if (!enforceFocus || !_this._isMounted || !_this.isTopModal()) {
        return;
      }
      var active = dom_helpers_1.activeElement(owner_document_1.ownerDocument(_this));
      var modal = _this.getDialogElement();
      if (modal && modal !== active && !dom_helpers_1.contains(modal, active)) {
        modal.focus();
      }
    };
    // instead of a ref, which might conflict with one the parent applied.
    _this.getDialogElement = function () {
      var node = _this.modalNode;
      return node && node.querySelector('[data-dialog-id="dialog-container"]');
    };
    _this.isTopModal = function () {
      return modalManager.isTopModal(_this);
    };
    _this.renderBackdrop = function () {
      var _a = _this.props,
        Transition = _a.transition,
        backdropTransitionTimeout = _a.backdropTransitionTimeout;
      var backdrop =
      // TODO: Fix this reference or delete it if it's not used
      // eslint-disable-next-line react/no-string-refs
      react_1.default.createElement("div", {
        ref: "backdrop",
        style: _this.props.backdropStyle,
        className: _this.props.backdropClassName,
        onClick: _this.handleBackdropClick
      });
      if (Transition) {
        backdrop = react_1.default.createElement(Transition, {
          transitionAppear: true,
          in: _this.props.show,
          timeout: backdropTransitionTimeout
        }, backdrop);
      }
      return backdrop;
    };
    _this.state = {
      exited: !props.show
    };
    return _this;
  }
  BaseModal.prototype.componentWillReceiveProps = function (nextProps) {
    if (nextProps.show) {
      this.setState({
        exited: false
      });
    } else if (!nextProps.transition) {
      // Otherwise let handleHidden take care of marking exited.
      this.setState({
        exited: true
      });
    }
  };
  BaseModal.prototype.componentWillUpdate = function (nextProps) {
    if (!this.props.show && nextProps.show) {
      this.checkForFocus();
    }
  };
  BaseModal.prototype.componentDidMount = function () {
    this._isMounted = true;
    if (this.props.show) {
      this.onShow();
    }
  };
  BaseModal.prototype.componentDidUpdate = function (prevProps) {
    var transition = this.props.transition;
    if (prevProps.show && !this.props.show && !transition) {
      // Otherwise handleHidden will call this.
      this.onHide();
    } else if (!prevProps.show && this.props.show) {
      this.onShow();
    }
  };
  BaseModal.prototype.componentWillUnmount = function () {
    var _a = this.props,
      show = _a.show,
      transition = _a.transition;
    this._isMounted = false;
    if (show || transition && !this.state.exited) {
      this.onHide();
    }
  };
  BaseModal.prototype.render = function () {
    var _a = this.props,
      show = _a.show,
      container = _a.container,
      children = _a.children,
      Transition = _a.transition,
      backdrop = _a.backdrop,
      dialogTransitionTimeout = _a.dialogTransitionTimeout,
      className = _a.className,
      style = _a.style,
      onExit = _a.onExit,
      onExiting = _a.onExiting,
      onEnter = _a.onEnter,
      onEntering = _a.onEntering,
      onEntered = _a.onEntered;
    var dialog = react_1.default.Children.only(children);
    var mountModal = show || Transition && !this.state.exited;
    if (!mountModal) {
      return null;
    }
    var _b = dialog.props,
      role = _b.role,
      tabIndex = _b.tabIndex;
    if (role === undefined || tabIndex === undefined) {
      dialog = react_1.default.cloneElement(dialog, {
        role: role === undefined ? 'document' : role,
        tabIndex: tabIndex == null ? '-1' : tabIndex
      });
    }
    if (Transition) {
      dialog = react_1.default.createElement(Transition, {
        transitionAppear: true,
        unmountOnExit: true,
        in: show,
        timeout: dialogTransitionTimeout,
        onExit: onExit,
        onExiting: onExiting,
        onExited: this.handleHidden,
        onEnter: onEnter,
        onEntering: onEntering,
        onEntered: onEntered
      }, dialog);
    }
    return react_1.default.createElement(portal_1.Portal, {
      ref: this.setMountNode,
      container: container
    }, react_1.default.createElement("div", {
      "data-no-focus-lock": true,
      ref: this.setModalNode,
      role: role || 'dialog',
      style: style,
      className: className
    }, backdrop && this.renderBackdrop(), dialog, react_1.default.createElement("span", {
      style: {
        display: 'none'
      }
    })));
  };
  BaseModal.propTypes = Object.assign({}, portal_1.Portal.propTypes, {
    show: prop_types_1.default.bool,
    container: prop_types_1.default.oneOfType([react_prop_types_1.componentOrElement, prop_types_1.default.func]),
    onShow: prop_types_1.default.func,
    onHide: prop_types_1.default.func,
    backdrop: prop_types_1.default.oneOfType([prop_types_1.default.bool, prop_types_1.default.oneOf(['static'])]),
    onEscapeKeyUp: prop_types_1.default.func,
    onBackdropClick: prop_types_1.default.func,
    backdropStyle: prop_types_1.default.object,
    backdropClassName: prop_types_1.default.string,
    containerClassName: prop_types_1.default.string,
    keyboard: prop_types_1.default.bool,
    transition: react_prop_types_1.elementType,
    dialogTransitionTimeout: prop_types_1.default.number,
    backdropTransitionTimeout: prop_types_1.default.number,
    autoFocus: prop_types_1.default.bool,
    enforceFocus: prop_types_1.default.bool,
    onEnter: prop_types_1.default.func,
    onEntering: prop_types_1.default.func,
    onEntered: prop_types_1.default.func,
    onExit: prop_types_1.default.func,
    onExiting: prop_types_1.default.func,
    onExited: prop_types_1.default.func
  });
  BaseModal.defaultProps = {
    show: false,
    backdrop: true,
    keyboard: true,
    autoFocus: true,
    enforceFocus: true
  };
  return BaseModal;
}(react_1.default.Component);
exports.BaseModal = BaseModal;